import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Text,
  Textarea,
} from '@chakra-ui/react'
import React, { useEffect, useMemo, useState } from 'react'
import Select, { SingleValue, StylesConfig } from 'react-select'
import { useDispatch } from 'react-redux'
import ModalComponent from '@/common/Modal'
import { getStoreNamesData } from '@/queue/ducks/selectors'
import { StoreNameType } from '@/queue/ducks/slice'
import { callToGetStoreNames } from '@/queue/ducks/slice'
import { callToAddDealerEvent, setAddDealerEventSuccess } from '@/redux/app-slice'
import { getIsAddDealerEventLoading, getAddDealerEventSuccess, getStoreData, getIsImpersonating } from '@/redux/app-selectors'


export type ActionOption = 'Called' | 'Emailed' | 'Profile Edit' | 'System' | 'Other'

const ACTION_OPTIONS: { label: string; value: ActionOption }[] = [
  { label: 'Called', value: 'Called' },
  { label: 'Emailed', value: 'Emailed' },
  { label: 'Profile Edit', value: 'Profile Edit' },
  { label: 'System', value: 'System' },
  { label: 'Other', value: 'Other' },
]

export interface AddEventFormValues {
  action: SingleValue<{ label: string; value: ActionOption }>
  store: SingleValue<StoreNameType>
  notes: string
  transferId: string
  orderNumber: string
}

const initialValues: AddEventFormValues = {
  action: null,
  store: null,
  notes: '',
  transferId: '',
  orderNumber: '',
}

const formLabelStyle = {
  color: '#333',
  fontFamily: 'Lato',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: 600,
  lineHeight: '21px',
}

const readOnlyLabelStyle = {
  color: 'rgba(51, 51, 51, 0.50)',
  fontFamily: 'Lato',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: 'normal',
}

const readOnlyValueStyle = {
  color: '#333',
  fontFamily: 'Lato',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: 700,
  lineHeight: 'normal',
}

const selectStyles: StylesConfig<StoreNameType, false> = {
  control: (styles) => ({
    ...styles,
    minHeight: '45px',
  }),
  menuList: (styles) => ({
    ...styles,
    maxHeight: '170px',
  }),
}

interface AddEventModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
  initialTransferId?: string
  initialOrderNumber?: string
  initialStoreId?: string
  initialStoreName?: string
  initialLicenseKey?: string
}

export default function AddEventModal({
  isOpen,
  onClose,
  onSuccess,
  initialTransferId,
  initialOrderNumber,
  initialStoreId,
  initialStoreName,
  initialLicenseKey,
}: AddEventModalProps) {
  const dispatch = useDispatch()
  const storeOptionsFromApi = getStoreNamesData() ?? []
  const isAddEventLoading = getIsAddDealerEventLoading()
  const addDealerEventSuccess = getAddDealerEventSuccess()
  const storeDetails = getStoreData()
  const isImpersonating = getIsImpersonating()

  const storeOptions = useMemo(() => {
    if (isImpersonating && storeDetails?.name != null && storeDetails?.store_id != null) {
      return [{ label: storeDetails.name, value: String(storeDetails.store_id) }]
    }
    return storeOptionsFromApi
  }, [isImpersonating, storeDetails?.name, storeDetails?.store_id, storeOptionsFromApi])

  const [formValues, setFormValues] = useState<AddEventFormValues>(initialValues)
  const [touched, setTouched] = useState({ action: false, notes: false })

  useEffect(() => {
    if (!isOpen) {
      setFormValues(initialValues)
      setTouched({ action: false, notes: false })
    } else {
      setFormValues((prev) => ({
        ...prev,
        transferId: initialTransferId ?? '',
        orderNumber: initialOrderNumber ?? '',
      }))
    }
  }, [isOpen, initialTransferId, initialOrderNumber])

  useEffect(() => {
    if (!isOpen) return
    if (!isImpersonating) {
      dispatch(
        callToGetStoreNames({
          orderBy: [],
          filter: ['is_external::inSet::true'],
          fetch_transfer_counts: false
        })
      )
    }
  }, [dispatch, isOpen, isImpersonating])

  useEffect(() => {
    if (!isOpen) return
    if (isImpersonating && storeDetails?.name != null && storeDetails?.store_id != null) {
      setFormValues((prev) => ({
        ...prev,
        store: { label: storeDetails.name, value: String(storeDetails.store_id) }
      }))
    } else if (initialStoreId?.trim()) {
      const storeOption = storeOptions.find((o) => String(o.value) === String(initialStoreId))
      if (storeOption) {
        setFormValues((prev) => ({ ...prev, store: storeOption }))
      } else {
        setFormValues((prev) => ({
          ...prev,
          store: {
            label: initialStoreName?.trim() || String(initialStoreId),
            value: String(initialStoreId)
          }
        }))
      }
    }
  }, [isOpen, initialStoreId, initialStoreName, storeOptions, isImpersonating, storeDetails?.name, storeDetails?.store_id])

  useEffect(() => {
    if (addDealerEventSuccess) {
      onSuccess?.()
      onClose()
      dispatch(setAddDealerEventSuccess(false))
    }
  }, [addDealerEventSuccess, onClose, onSuccess, dispatch])

  const isFormValid = useMemo(() => {
    return (
      formValues.action != null &&
      formValues.notes.trim() !== ''
    )
  }, [formValues.action, formValues.notes])

  const handleAddEvent = () => {
    if (!isFormValid) return
    dispatch(
      callToAddDealerEvent({
        license_key: initialLicenseKey?.trim() || undefined,
        transfer_id: formValues.transferId.trim() || undefined,
        order_number: formValues.orderNumber.trim() || undefined,
        store_id: formValues.store?.value,
        action: formValues.action!.value.toLowerCase(),
        notes: formValues.notes.trim(),
      })
    )
  }

  const footer = (
    <Box display="flex" justifyContent="flex-end" gap="3" marginTop="-30px">
      <Button
        onClick={onClose}
        isDisabled={isAddEventLoading}
        background="#ffffff"
        color="#0C1E46"
        border="1px solid #0C1E46"
        padding="8px 11px"
        _hover={{ bg: 'gray.50' }}
      >
        Cancel
      </Button>
      <Button
        onClick={handleAddEvent}
        isDisabled={!isFormValid || isAddEventLoading}
        isLoading={isAddEventLoading}
        loadingText="Adding..."
        background="#0C1E46"
        color="#FFF"
        padding="8px 20px"
        _hover={{ bg: '#0C1E46', opacity: 0.3 }}
      >
        Add Event
      </Button>
    </Box>
  )

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={onClose}
      title="Add Event"
      variant={2}
      className="add-event-modal"
      maxWidth="552px"
      footer={footer}
    >
      <Box display="flex" flexDirection="column">
        <Box display="flex" gap="24px" pb="24px" flexWrap="wrap">
          <Box flex="1" minW="140px">
            <Text as="span" sx={readOnlyLabelStyle}>
              Transfer ID:
            </Text>
            <Text as="span" sx={readOnlyValueStyle} display="block" mt="4px">
              {formValues.transferId.trim() || 'N/A'}
            </Text>
          </Box>
          <Box flex="1" minW="140px">
            <Text as="span" sx={readOnlyLabelStyle}>
              Order #:
            </Text>
            <Text as="span" sx={readOnlyValueStyle} display="block" mt="4px">
              {formValues.orderNumber.trim() || 'N/A'}
            </Text>
          </Box>
        </Box>

        <FormControl isRequired pb="24px" isInvalid={touched.action && formValues.action == null}>
          <FormLabel sx={formLabelStyle}>Action</FormLabel>
          <Select<{ label: string; value: ActionOption }>
            value={formValues.action}
            options={ACTION_OPTIONS}
            placeholder="Select action"
            isClearable
            onChange={(option) =>
              setFormValues((prev) => ({ ...prev, action: option }))
            }
            onBlur={() => setTouched((prev) => ({ ...prev, action: true }))}
            styles={selectStyles as StylesConfig<{ label: string; value: ActionOption }, false>}
          />
          <FormErrorMessage>Please select an action</FormErrorMessage>
        </FormControl>

        <FormControl pb="24px">
          <FormLabel sx={formLabelStyle}>Store</FormLabel>
          <Select<StoreNameType>
            value={formValues.store}
            options={storeOptions}
            placeholder="Select a store"
            isClearable={
              !initialStoreId?.trim() &&
              !isImpersonating &&
              !formValues.transferId.trim() &&
              !formValues.orderNumber.trim()
            }
            isDisabled={
              Boolean(initialStoreId?.trim()) ||
              isImpersonating ||
              formValues.transferId.trim() !== '' ||
              formValues.orderNumber.trim() !== ''
            }
            getOptionLabel={(o) => o.label}
            getOptionValue={(o) => o.value}
            onChange={(option) =>
              setFormValues((prev) => ({ ...prev, store: option }))
            }
            styles={selectStyles}
          />
          <FormHelperText
            mt="1"
            sx={{
              color: 'rgba(51, 51, 51, 0.50)',
              fontFamily: 'Lato',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: 'normal',
            }}
          >
            Store will be auto-populated with transfer ID
          </FormHelperText>
        </FormControl>

        <FormControl isRequired pb="24px" isInvalid={touched.notes && formValues.notes.trim() === ''}>
          <FormLabel sx={formLabelStyle}>Notes</FormLabel>
          <Textarea
            value={formValues.notes}
            onChange={(e) =>
              setFormValues((prev) => ({ ...prev, notes: e.target.value }))
            }
            onBlur={() => setTouched((prev) => ({ ...prev, notes: true }))}
            placeholder="Enter event notes"
            minHeight="100px"
            resize="vertical"
          />
          <FormErrorMessage>Notes are required</FormErrorMessage>
        </FormControl>
      </Box>
    </ModalComponent>
  )
}
